/**
 * O detalhe de uma organização na gestão (#765, fase 3, #767).
 *
 * Junta o cadastro, o contrato, o uso, os administradores e o histórico de uma
 * organização. Só o Super Admin dentro da Cliqfy chega aqui (fase 1, #1013).
 *
 * ── A tela nasceu antes do back ───────────────────────────────────────────
 *
 * Como a lista (decisão de 26/10/2026): fica pronta e recebe tudo pelas props.
 * Quem a monta consulta a API, salva o contrato e o cadastro, e avisa
 * ("Contrato salvo. A mudança foi para o histórico.").
 *
 * ── Tudo que muda dinheiro ou limite fica no histórico ────────────────────
 *
 * A entrada que acabou de entrar (`novaNoHistorico`) destaca, para quem salvou
 * achar a própria mudança com antes e depois.
 */

import { useState, type ReactNode } from 'react';
import {
  Avatar,
  Botao,
  CabecalhoDeTela,
  Cartao,
  Icone,
  ListaDoHistorico,
  Medidor,
  Segmentado,
  Selo,
  type OpcaoSegmentada,
  type TomSelo,
} from '@/shared/ui';
import { mascararCnpj } from '@/shared/lib/cnpj';
import { comUmaCasa, diaMesAno, dinheiroComCentavos, telefoneParaLer } from '@/shared/lib/formato';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import type {
  EntradaDoHistorico,
  MudancaDoCadastro,
  MudancaDoContrato,
  OrganizacaoDetalhada,
  SituacaoDaOrganizacao,
} from '../model/organizacao-detalhada';
import { fraseDoEvento, porQuem, quandoEmPalavras } from '../lib/historico';
import type { EstadoDaRetomada } from '../lib/retomada';
import { FaixaDaCriacao } from './faixa-da-criacao';
import { PainelDoCadastro } from './painel-do-cadastro';
import { PainelDoContrato } from './painel-do-contrato';
import './organizacao.css';

export type AbaDoDetalhe = 'resumo' | 'contrato' | 'uso' | 'administradores' | 'historico';

export type TelaDaOrganizacaoProps = {
  /** O que veio do servidor; sem nada enquanto carrega. */
  organizacao: OrganizacaoDetalhada | undefined;
  historico: readonly EntradaDoHistorico[] | undefined;
  /** O erro da consulta, quando ela falhou e não há o que mostrar. */
  erro?: unknown;
  /** O erro do histórico: a aba mostra o motivo e oferece repetir, em vez de carregar para sempre. */
  erroDoHistorico?: unknown;
  aoRepetir: () => void;
  aoVoltar: () => void;
  /** Salva o contrato; rejeita com o erro da API, que o painel mostra. */
  aoSalvarContrato: (mudanca: MudancaDoContrato) => Promise<void>;
  /** Salva o cadastro ou o contato financeiro (#769). Sem ele, o resumo só mostra. */
  aoSalvarCadastro?: (mudanca: MudancaDoCadastro) => Promise<void>;
  /**
   * Abrem o diálogo de cada ação, que quem monta a tela cuida. Sem os três de
   * encerrar, reabrir e excluir, a zona de perigo não aparece.
   */
  aoEncerrar?: () => void;
  aoReabrir?: () => void;
  aoExcluir?: () => void;
  /**
   * Retoma a criação parada com o pedido guardado (#769), que quem monta a tela
   * conduz. Sem ele, a faixa da criação parada só avisa.
   */
  aoRetomar?: () => void;
  /** O estado da retomada: retomando, refazer pelo formulário, o que não terminou ou a falha. */
  retomada?: EstadoDaRetomada | null;
  /** Sem o pedido guardado, ou com o administrador que não serve, a faixa leva ao formulário. */
  aoAbrirNovaOrganizacao?: () => void;
  /** O que sobrou para fazer depois de uma retomada que terminou, como a API devolve. */
  pendenciasDaCriacao?: readonly string[];
  /** O id da entrada que acabou de entrar no histórico: ela destaca. */
  novaNoHistorico?: string | null;
  /** O relógio do "agora" do histórico; os testes fixam. */
  agora?: number;
};

const ABAS: OpcaoSegmentada<AbaDoDetalhe>[] = [
  { valor: 'resumo', rotulo: 'Resumo' },
  { valor: 'contrato', rotulo: 'Contrato' },
  { valor: 'uso', rotulo: 'Uso' },
  { valor: 'administradores', rotulo: 'Administradores' },
  { valor: 'historico', rotulo: 'Histórico' },
];

const SITUACOES: Readonly<
  Record<SituacaoDaOrganizacao, { readonly rotulo: string; readonly tom: TomSelo }>
> = {
  ativa: { rotulo: 'Ativa', tom: 'ok' },
  em_criacao: { rotulo: 'Em criação', tom: 'info' },
  suspensa: { rotulo: 'Suspensa', tom: 'atencao' },
  encerrada: { rotulo: 'Encerrada', tom: 'neutro' },
};

/** Os GB com uma casa; o que arredondaria para zero vira "menos de 0,1". */
const gbParaLer = (gb: number): string => (gb < 0.05 ? 'menos de 0,1' : comUmaCasa(gb));

export function TelaDaOrganizacao({
  organizacao,
  historico,
  erro,
  erroDoHistorico,
  aoRepetir,
  aoVoltar,
  aoSalvarContrato,
  aoSalvarCadastro,
  aoEncerrar,
  aoReabrir,
  aoExcluir,
  aoRetomar,
  retomada = null,
  aoAbrirNovaOrganizacao,
  pendenciasDaCriacao = [],
  novaNoHistorico = null,
  agora = Date.now(),
}: TelaDaOrganizacaoProps): ReactNode {
  // O contrato abre primeiro: é o que a equipe vem mudar quando entra aqui.
  const [aba, definirAba] = useState<AbaDoDetalhe>('contrato');

  if (erro && !organizacao) return <Erro erro={erro} aoRepetir={aoRepetir} />;
  if (!organizacao) return <Carregando forma="ficha" rotulo="Carregando a organização" />;

  const situacao = SITUACOES[organizacao.situacao];
  const { cadastro, contrato, plano, uso } = organizacao;

  return (
    <div className="organizacao">
      <CabecalhoDeTela
        variante="identidade"
        titulo={organizacao.nome}
        aoLadoDoTitulo={<Selo tom={situacao.tom}>{situacao.rotulo}</Selo>}
        voltar={
          <span className="cab__voltar">
            <Botao
              variante="fantasma"
              icone
              aria-label="Voltar para as organizações"
              title="Voltar"
              onClick={aoVoltar}
            >
              <Icone nome="voltar" />
            </Botao>
          </span>
        }
        retrato={
          <span className="cab__retrato">
            <Avatar nome={organizacao.nome} semente={organizacao.id} tamanho={48} cheio />
          </span>
        }
        meta={
          <>
            <b>{organizacao.identificador}</b>
            {cadastro.cnpj ? <span>CNPJ {mascararCnpj(cadastro.cnpj)}</span> : null}
            {cadastro.segmento ? <span>{cadastro.segmento.nome}</span> : null}
            <span>desde {diaMesAno(organizacao.desde)}</span>
          </>
        }
      />
      <div className="organizacao__corpo">
        {organizacao.situacao === 'em_criacao' ? (
          <FaixaDaCriacao
            parouEm={organizacao.parouEm}
            pedidoGuardadoAte={organizacao.pedidoGuardadoAte}
            retomada={retomada}
            {...(aoRetomar ? { aoRetomar } : {})}
            {...(aoAbrirNovaOrganizacao ? { aoAbrirNovaOrganizacao } : {})}
          />
        ) : null}

        {/* Fora da faixa, que some quando a organização fica ativa. */}
        {pendenciasDaCriacao.length > 0 ? (
          <Cartao variante="folgado" como="section" aria-labelledby="organizacao-falta">
            <h3 id="organizacao-falta" className="organizacao__titulo-do-painel">
              O que ainda falta fazer
            </h3>
            <ul className="organizacao__pendencias">
              {pendenciasDaCriacao.map((pendencia) => (
                <li key={pendencia}>{pendencia}</li>
              ))}
            </ul>
          </Cartao>
        ) : null}

        <div className="organizacao__resumo">
          <Cartao>
            <span className="organizacao__rotulo">Plano</span>
            <strong className="organizacao__valor">{plano?.nome ?? 'Sem plano'}</strong>
          </Cartao>
          <Cartao>
            <span className="organizacao__rotulo">Usuários</span>
            {contrato ? (
              <Medidor
                nome="Usuários"
                usado={uso.usuariosEmUso}
                total={contrato.usuariosContratados}
                rotulo={`${uso.usuariosEmUso} de ${contrato.usuariosContratados}`}
              />
            ) : (
              <strong className="organizacao__valor">{uso.usuariosEmUso} em uso</strong>
            )}
          </Cartao>
          <Cartao>
            <span className="organizacao__rotulo">Armazenamento</span>
            {contrato && uso.armazenamentoGb !== null ? (
              <Medidor
                nome="Armazenamento"
                usado={uso.armazenamentoGb}
                total={contrato.armazenamentoContratadoGb}
                rotulo={`${comUmaCasa(uso.armazenamentoGb)} de ${comUmaCasa(contrato.armazenamentoContratadoGb)} GB`}
              />
            ) : (
              <strong className="organizacao__valor">
                {contrato ? `${comUmaCasa(contrato.armazenamentoContratadoGb)} GB` : 'Sem contrato'}
              </strong>
            )}
            {uso.contaDoAtendimentoDividida ? (
              <p className="organizacao__nota">Conta do Atendimento dividida: veja a aba Uso.</p>
            ) : null}
          </Cartao>
          <Cartao>
            <span className="organizacao__rotulo">Mensalidade</span>
            <strong className="organizacao__valor">
              {dinheiroComCentavos(contrato?.mensalidade ?? null) ?? 'A definir'}
            </strong>
          </Cartao>
        </div>

        <Segmentado
          rotulo="Partes da organização"
          aparencia="sublinhado"
          opcoes={ABAS}
          escolhida={aba}
          aoEscolher={definirAba}
        />

        {aba === 'resumo' ? (
          <div role="tabpanel" aria-label="Resumo" className="organizacao__painel">
            <PainelDoCadastro organizacao={organizacao} aoSalvar={aoSalvarCadastro} />
          </div>
        ) : null}

        {aba === 'contrato' ? (
          <div role="tabpanel" aria-label="Contrato" className="organizacao__painel">
            <PainelDoContrato organizacao={organizacao} aoSalvar={aoSalvarContrato} />
          </div>
        ) : null}

        {aba === 'uso' ? (
          <div role="tabpanel" aria-label="Uso" className="organizacao__painel">
            <Cartao variante="folgado">
              <div className="organizacao__uso">
                <span className="organizacao__rotulo">Usuários</span>
                {contrato ? (
                  <Medidor
                    nome="Usuários"
                    usado={uso.usuariosEmUso}
                    total={contrato.usuariosContratados}
                    rotulo={`${uso.usuariosEmUso} em uso de ${contrato.usuariosContratados} contratados`}
                  />
                ) : (
                  <span>{uso.usuariosEmUso} em uso</span>
                )}
              </div>
              <div className="organizacao__uso">
                <span className="organizacao__rotulo">Armazenamento</span>
                {contrato && uso.armazenamentoGb !== null ? (
                  <Medidor
                    nome="Armazenamento"
                    usado={uso.armazenamentoGb}
                    total={contrato.armazenamentoContratadoGb}
                    rotulo={`${comUmaCasa(uso.armazenamentoGb)} GB em uso de ${comUmaCasa(contrato.armazenamentoContratadoGb)} GB`}
                  />
                ) : (
                  <span>
                    {contrato
                      ? `${comUmaCasa(contrato.armazenamentoContratadoGb)} GB contratados`
                      : 'Sem contrato'}
                  </span>
                )}
                {/* A conta dividida (#1161): é exposição, e não só cota. */}
                {uso.contaDoAtendimentoDividida ? (
                  <p className="organizacao__nota">
                    A conta do Atendimento desta organização é dividida com outra: as duas recebem
                    as conversas e os contatos uma da outra. Os anexos das conversas
                    {uso.armazenamentoForaDaCotaGb > 0
                      ? ` (${gbParaLer(uso.armazenamentoForaDaCotaGb)} GB)`
                      : ''}{' '}
                    ficam fora da cota até a conta ser separada.
                  </p>
                ) : null}
              </div>
            </Cartao>
          </div>
        ) : null}

        {aba === 'administradores' ? (
          <div role="tabpanel" aria-label="Administradores" className="organizacao__painel">
            {organizacao.exclusao ? (
              <Vazio
                titulo="Organização excluída"
                porque="A gestão dela acabou: os administradores não aparecem mais."
              />
            ) : organizacao.administradores.length === 0 ? (
              <Vazio
                titulo="Nenhum administrador"
                porque="Sem administrador, ninguém da organização convida pessoas nem muda as configurações."
              />
            ) : (
              <ul className="organizacao__administradores" aria-label="Administradores">
                {organizacao.administradores.map((pessoa) => (
                  <li key={pessoa.id} className="organizacao__administrador">
                    <Avatar nome={pessoa.nome} semente={pessoa.id} tamanho={28} />
                    <span className="organizacao__quem">
                      {pessoa.nome}
                      <span className="organizacao__apoio">
                        {pessoa.email}
                        {pessoa.telefone ? ` · ${telefoneParaLer(pessoa.telefone)}` : null}
                      </span>
                    </span>
                    <span className="organizacao__papel">Administrador</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {aba === 'historico' ? (
          <div role="tabpanel" aria-label="Histórico" className="organizacao__painel">
            <Historico
              historico={historico}
              erro={erroDoHistorico}
              aoRepetir={aoRepetir}
              novaNoHistorico={novaNoHistorico}
              agora={agora}
            />
          </div>
        ) : null}

        {organizacao.daOperacao ? (
          <section className="organizacao__zona" aria-label="Organização da operação">
            <p>
              É a organização da operação da Cliqfy: não se encerra nem se exclui pela gestão,
              porque sem ela ninguém da equipe entra.
            </p>
            {/* Encerrada antes desta regra: reabrir, de outra organização, é a saída. */}
            {organizacao.situacao === 'encerrada' && aoReabrir ? (
              <Botao miudo onClick={aoReabrir}>
                Reabrir organização
              </Botao>
            ) : null}
          </section>
        ) : organizacao.exclusao ? (
          <Excluida exclusao={organizacao.exclusao} aoExcluir={aoExcluir} />
        ) : organizacao.situacao === 'em_criacao' ? null : aoEncerrar && aoReabrir && aoExcluir ? (
          <ZonaDePerigo
            encerrada={organizacao.situacao === 'encerrada'}
            aoEncerrar={aoEncerrar}
            aoReabrir={aoReabrir}
            aoExcluir={aoExcluir}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * A excluída: quando, e até quando o que o CliqCRM guarda dela deve sair. A
 * purga ainda é manual, e a ficha diz isso, para ninguém responder ao cliente
 * que os dados já saíram. Se alguma parte não terminou, excluir de novo tenta
 * o que falta.
 */
function Excluida({
  exclusao,
  aoExcluir,
}: {
  exclusao: NonNullable<OrganizacaoDetalhada['exclusao']>;
  aoExcluir: (() => void) | undefined;
}): ReactNode {
  const quando = diaMesAno(exclusao.em);
  // Sem afirmar o que não aconteceu: na incompleta, a conta de atendimento pode continuar de pé.
  const oQueAconteceu = !exclusao.incompleta
    ? exclusao.contaDoAtendimentoFicou
      ? `Excluída em ${quando}: o acesso dela foi apagado, e a conta de atendimento ficou, ` +
        'porque não é só dela. O que fazer com a conta está abaixo.'
      : `Excluída em ${quando}: o acesso e a conta de atendimento dela foram apagados.`
    : exclusao.partesPendentes.length > 0
      ? `Excluída em ${quando}, mas não terminou em: ${exclusao.partesPendentes.join(', ')}. ` +
        'Excluir de novo tenta o que falta.'
      : `A exclusão começou em ${quando} e não chegou ao fim. Excluir de novo tenta o que falta.`;
  return (
    <section className="organizacao__zona" aria-label="Excluída de vez">
      <p>
        {oQueAconteceu} Tudo o que o CliqCRM guarda dela (conversas, clientes, comercial, arquivos e
        eventos) deve sair até {diaMesAno(exclusao.purgaAte)}, pelo prazo de retenção. Por enquanto,
        a purga é feita à mão.
      </p>
      {exclusao.pendencias.length > 0 ? (
        <ul className="organizacao__pendencias" aria-label="O que fica para fazer depois">
          {exclusao.pendencias.map((pendencia) => (
            <li key={pendencia}>{pendencia}</li>
          ))}
        </ul>
      ) : null}
      {exclusao.incompleta && aoExcluir ? (
        <Botao variante="perigo" miudo onClick={aoExcluir}>
          Excluir de novo
        </Botao>
      ) : null}
    </section>
  );
}

function Historico({
  historico,
  erro,
  aoRepetir,
  novaNoHistorico,
  agora,
}: {
  historico: readonly EntradaDoHistorico[] | undefined;
  erro: unknown;
  aoRepetir: () => void;
  novaNoHistorico: string | null;
  agora: number;
}): ReactNode {
  if (erro && !historico) {
    return (
      <Erro erro={erro} aoRepetir={aoRepetir} titulo="Não foi possível carregar o histórico" />
    );
  }
  if (!historico) return <Carregando forma="lista" linhas={4} rotulo="Carregando o histórico" />;
  if (historico.length === 0) {
    return (
      <Vazio
        titulo="Nada no histórico ainda"
        porque="Cada mudança de plano, limite, preço ou cota entra aqui, com antes e depois."
      />
    );
  }
  return (
    <Cartao variante="folgado">
      <ListaDoHistorico
        rotulo="Histórico da organização"
        entradas={historico.map((entrada) => ({
          chave: entrada.id,
          instante: entrada.quando,
          quando: quandoEmPalavras(entrada.quando, agora),
          ...fraseDoEvento(entrada.evento),
          por: porQuem(entrada.por),
          nova: entrada.id === novaNoHistorico,
        }))}
      />
    </Cartao>
  );
}

/**
 * Encerrar, reabrir e excluir usam as rotas que já existem, com as mesmas
 * barreiras: excluir exige a organização encerrada, justificativa e segundo
 * fator. A zona só diz o que cada um faz e entrega o pedido a quem monta a tela.
 */
function ZonaDePerigo({
  encerrada,
  aoEncerrar,
  aoReabrir,
  aoExcluir,
}: {
  encerrada: boolean;
  aoEncerrar: () => void;
  aoReabrir: () => void;
  aoExcluir: () => void;
}): ReactNode {
  return (
    <section className="organizacao__zona" aria-label="Encerrar ou excluir">
      {encerrada ? (
        <>
          <p>
            Encerrada: o acesso e as conexões estão desligados, e nada foi apagado. Reabrir desfaz.
            Excluir apaga de vez, com justificativa e segundo fator.
          </p>
          <span className="organizacao__acoes-da-zona">
            <Botao miudo onClick={aoReabrir}>
              Reabrir organização
            </Botao>
            <Botao variante="perigo" miudo onClick={aoExcluir}>
              Excluir organização
            </Botao>
          </span>
        </>
      ) : (
        <>
          <p>
            Encerrar desliga o acesso e as conexões sem apagar nada, e reabrir desfaz. Excluir só
            depois de encerrar, com justificativa e segundo fator.
          </p>
          <Botao variante="perigo" miudo onClick={aoEncerrar}>
            Encerrar organização
          </Botao>
        </>
      )}
    </section>
  );
}
